// Executor task-list filtering (§1.6 Build 3). Pure, client-side narrowing of the
// tasks that already arrive in one API response — no server involvement. Extracted
// as a pure function so the predicates are unit-tested directly (not via the page).
//
// Status note (Build 3): live statuses are not_started / done / dismissed. `waiting`
// and `in_progress` don't occur until Build 6, and auto-resolve is the gate-derived
// `isAutoResolved` flag (not the status) until Build 5 — so the Waiting filter is
// legitimately empty for now, handled by the page's honest empty state.

export type FilterKey = "All" | "Critical" | "To do" | "Waiting" | "Done";
export const FILTERS: FilterKey[] = ["All", "Critical", "To do", "Waiting", "Done"];

export interface FilterableTask {
  title: string;
  status: string;
  consequenceTier: string;
  isAutoResolved: boolean;
  // s148 FORM-SEARCH-1: gov-form(s) this task carries (from the server task-list). Optional-guarded so
  // older cached payloads (pre-s148) still filter (they just won't match by form id/title).
  govForms?: { formId: string; title: string }[];
}

// "Resolved without action" = auto-resolved by a gate, or dismissed by the executor.
// These appear only under All (in their strips); the attention filters exclude them.
const isResolvedOut = (t: FilterableTask): boolean => t.isAutoResolved || t.status === "dismissed";

export function matchesFilter(t: FilterableTask, filter: FilterKey): boolean {
  switch (filter) {
    case "All":
      return true;
    case "Critical": // critical *remaining* — mirrors the summary pill
      return t.consequenceTier === "critical" && !isResolvedOut(t) && t.status !== "done";
    case "To do":
      return (t.status === "not_started" || t.status === "in_progress") && !t.isAutoResolved;
    case "Waiting":
      return t.status === "waiting";
    case "Done":
      return t.status === "done";
  }
}

/**
 * Narrow the task list.
 * - Empty query: filter by the active segment tab (matchesFilter).
 * - Non-empty query (D-130.7): search OVERRIDES the tab — match a case-insensitive substring across
 *   ALL tasks, ignoring the active filter AND including resolved-out (auto-resolved / dismissed) tasks,
 *   so a task can always be found. The page renders resolved matches in the (auto-expanded) resolved
 *   zone with a "Doesn't apply" tag.
 * - s148 FORM-SEARCH-1: the query also matches a task's associated gov-form id AND title, so an executor
 *   holding paper that names a form ("74A", "ISP-1300", "1201", "Survivor's Pension") finds the task.
 */
export function filterTasks<T extends FilterableTask>(tasks: T[], filter: FilterKey, query: string): T[] {
  const q = query.trim().toLowerCase();
  if (q) return tasks.filter((t) => taskSearchHaystack(t).includes(q));
  return tasks.filter((t) => matchesFilter(t, filter));
}

// The searchable text for a task: its title plus every associated gov-form id + title. A plain substring
// of the whole handles "74A", "ISP-1300", "1300" (⊂ "ISP-1300"), and "Survivor's Pension" alike.
function taskSearchHaystack(t: FilterableTask): string {
  const forms = (t.govForms ?? []).map((f) => `${f.formId} ${f.title}`).join(" ");
  return `${t.title} ${forms}`.toLowerCase();
}
